import { Location } from "@angular/common";
import { Component, computed, OnDestroy, OnInit, Signal, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Dialog } from "@angular/cdk/dialog";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { SkeletonModule } from "primeng/skeleton";
import { JobIssue, Media } from "../../../../core/database/db";
import { SELF_ASSIGNED_VENDOR, VendorDTO } from "../../../vendors/models/VendorDTO";
import ScheduleDetailHeader from "../shared/schedule-detail-header/schedule-detail-header.component";
import { TabBarComponent, TabOption } from "../../../../shared/components/tab-bar/tab-bar.component";
import { PunchlistListComponent } from "../../../../shared/components/punchlist/punchlist.component";
import { ChecklistComponent } from "../../../../shared/components/checklist/checklist.component";
import { ChecklistComponentEntry } from "../../../../shared/services/checklist.service";
import { DiscussAndDemonstrateIssueDrawerComponent, DiscussAndDemonstrateIssueSaveMode } from "./components/discuss-and-demonstrate-issue-drawer/discuss-and-demonstrate-issue-drawer.component";
import { ImagesModalComponent } from "../../../media/components/images-modal/images-modal.component";
import { BqaImageItem } from "../../../media/components/images-modal/bqa-image-item";
import { BlobService } from "../../../../shared/services/blob.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { ExtendedRouter } from "../../../../shared/services/extended-router.service";
import { BqStepDataServiceFactory } from "../../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { BqInspectionDiscussAndDemonstrateDataService } from "../../services/bq-inspection-discuss-and-demonstrate-data.service";
import { UNASSIGNED_AREA } from "../../../areas/models/AreaDTO";
import { getDiscussAndDemonstrateHeaderTitle } from "../discuss-and-demonstrate-header-title";
import { resolveLastUsedRoomFromIssues } from "../../../jobIssues/utils/last-used-room.util";

enum DiscussAndDemonstrateTab {
  Items = 0,
  Checklist = 1
}

type AreaNameIssue = JobIssue & { areaName?: string };

/**
 * Tabbed "punchlist + home-feature checklist" walkthrough page shared by BQ1 (Pre-Construction),
 * BQ5 (Pre-Drywall) and BQ8 (Pre-Closing). Every read here comes from the BqStepDataService's
 * already-loaded signals (populated by the respective BqXRootComponent), and every write goes through
 * BqInspectionDiscussAndDemonstrateDataService - this component never touches IndexedDB directly.
 */
@Component({
  selector: "app-job-discuss-and-demonstrate-checklist",
  standalone: true,
  imports: [
    ScheduleDetailHeader,
    SkeletonModule,
    TabBarComponent,
    PunchlistListComponent,
    ChecklistComponent,
    DiscussAndDemonstrateIssueDrawerComponent
  ],
  providers: [BqStepDataServiceFactory],
  templateUrl: "./job-discuss-and-demonstrate-checklist.component.html",
  styleUrl: "./job-discuss-and-demonstrate-checklist.component.scss"
})
export class JobDiscussAndDemonstrateChecklistComponent implements OnInit, OnDestroy {
  private bqStepDataService!: BqInspectionDiscussAndDemonstrateDataService;
  private readonly objectUrls = new Set<string>();

  readonly Tab = DiscussAndDemonstrateTab;
  readonly tabOptions: TabOption<DiscussAndDemonstrateTab>[] = [
    { label: "Items", value: DiscussAndDemonstrateTab.Items },
    { label: "Checklist", value: DiscussAndDemonstrateTab.Checklist }
  ];

  currentTab = signal<DiscussAndDemonstrateTab>(DiscussAndDemonstrateTab.Items);
  touched = signal(false);
  addingIssue = signal(false);
  issueBeingEdited = signal<JobIssue | undefined>(undefined);
  newIssueId = signal(-1);

  dataLoaded = computed(() => this.bqStepDataService?.hasData() ?? false);
  job = computed(() => this.bqStepDataService?.job());
  headerTitle = computed(() => getDiscussAndDemonstrateHeaderTitle(this.bqStepDataService?.bqStep() ?? ""));
  subtitle = computed(() => {
    const lot = this.bqStepDataService?.lot();
    return lot ? `Lot ${lot.lotBlock} - ${lot.streetAddress}` : "";
  });

  checklistGroups = computed(() => this.bqStepDataService?.checklistGroups() ?? []);
  checklistComplete = computed(() => this.bqStepDataService?.checklistComplete() ?? false);

  /** Pre-selects the most recently used room when logging a new issue, mirroring the single-area checklist. */
  issueDrawerInitialLotAreaIdentifier = computed(() => {
    const resolved = resolveLastUsedRoomFromIssues(this.bqStepDataService?.jobIssues() ?? []);
    if (!resolved) {
      return undefined;
    }

    return (this.bqStepDataService?.availableRooms() ?? []).some(room => room.identifier === resolved) ? resolved : undefined;
  });

  private readonly areaNameByIdentifier = computed<Record<string, string>>(() => {
    const dict: Record<string, string> = {};
    const areas = [...(this.bqStepDataService?.areas() ?? []), ...(this.bqStepDataService?.availableRooms() ?? [])];
    for (const area of areas) {
      dict[area.identifier] = area.name;
    }
    return dict;
  });

  readonly openIssues = computed<AreaNameIssue[]>(() => {
    const areaNameByIdentifier = this.areaNameByIdentifier();
    return (this.bqStepDataService?.openIssues() ?? [])
      .map(issue => ({
        ...issue,
        areaName: (issue.lotAreaIdentifier && areaNameByIdentifier[issue.lotAreaIdentifier]) || UNASSIGNED_AREA.name
      }))
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  });

  areaNames = computed<string[]>(() => {
    const names = new Set(this.openIssues().map(issue => issue.areaName)
      .filter((name): name is string => !!name));
    return [...names].sort();
  });

  issuesByArea = computed<Record<string, JobIssue[]>>(() => {
    const dict: Record<string, JobIssue[]> = {};
    for (const issue of this.openIssues()) {
      if (!issue.areaName) {
        continue;
      }
      (dict[issue.areaName] ??= []).push(issue);
    }
    return dict;
  });

  imagesOfIssue = computed<Record<number, Signal<(SafeUrl | undefined)[]>>>(() => {
    const dict: Record<number, Signal<(SafeUrl | undefined)[]>> = {};
    for (const issue of this.openIssues()) {
      if (issue.id === undefined) {
        continue;
      }
      dict[issue.id] = computed(() => this.resolveDisplayImages(issue.identifier));
    }
    return dict;
  });

  private galleryImagesOfIssue = computed<Record<number, Signal<BqaImageItem[]>>>(() => {
    const dict: Record<number, Signal<BqaImageItem[]>> = {};
    for (const issue of this.openIssues()) {
      if (issue.id === undefined) {
        continue;
      }
      dict[issue.id] = computed(() => this.resolveGalleryImages(issue.identifier));
    }
    return dict;
  });

  vendorOfIssue = computed<Record<number, Signal<VendorDTO>>>(() => {
    const dict: Record<number, Signal<VendorDTO>> = {};
    for (const issue of this.openIssues()) {
      if (issue.id === undefined) {
        continue;
      }
      const vendor = this.resolveVendorForIssue(issue);
      if (vendor) {
        dict[issue.id] = computed(() => this.resolveVendorForIssue(issue)!);
      }
    }
    return dict;
  });

  constructor(
    private route: ActivatedRoute,
    private router: ExtendedRouter,
    private location: Location,
    private sanitizer: DomSanitizer,
    private blobService: BlobService,
    private toastService: UiToastService,
    private dialog: Dialog,
    private bqStepDataServiceFactory: BqStepDataServiceFactory
  ) { }

  ngOnInit(): void {
    const bqStep = this.route.snapshot.parent?.data["bqStep"] as string;
    this.bqStepDataService = this.bqStepDataServiceFactory.getService<BqInspectionDiscussAndDemonstrateDataService>(bqStep);
  }

  ngOnDestroy(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  changeTab(tab: DiscussAndDemonstrateTab): void {
    this.currentTab.set(tab);
  }

  openDrawer(issue?: JobIssue): void {
    this.issueBeingEdited.set(issue);
    this.addingIssue.set(true);
  }

  closeDrawer(): void {
    this.issueBeingEdited.set(undefined);
    this.addingIssue.set(false);
  }

  // DiscussAndDemonstrateIssueDrawerComponent already persisted this issue via the data service; just highlight/close.
  handleIssueSaved(saved: { issue: JobIssue; newIssue: boolean; saveMode: DiscussAndDemonstrateIssueSaveMode }): void {
    if (saved.newIssue && saved.issue.id !== undefined) {
      this.highlightIssue(saved.issue.id);
    }

    // Save & Add Another keeps the drawer open, already reset for the next item.
    if (saved.saveMode === "addAnother") {
      return;
    }

    this.closeDrawer();
  }

  // DiscussAndDemonstrateIssueDrawerComponent already deleted this issue via the data service; just close.
  handleIssueDeleted(): void {
    this.closeDrawer();
  }

  highlightIssue(issueId: number): void {
    this.currentTab.set(DiscussAndDemonstrateTab.Items);
    this.newIssueId.set(issueId);
    setTimeout(() => {
      document.getElementById(`issue-card-${issueId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        this.newIssueId.set(-1);
      }, 2500);
    }, 1);
  }

  openImagesModal(issueId: number, imageIndex: number): void {
    const galleryImages = this.galleryImagesOfIssue()[issueId];
    if (!galleryImages) {
      return;
    }

    this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: galleryImages(),
        index: imageIndex,
        isIssue: true,
        onDeleted: (fileIdentifier: string) => this.handleImageDeleted(issueId, fileIdentifier),
        backdropClass: "images-modal-backdrop"
      }
    });
  }

  async persistChecklistItemToggle(entry: ChecklistComponentEntry, isComplete: boolean): Promise<void> {
    await this.bqStepDataService.updateChecklistComponentCompletion(entry, isComplete);
  }

  updateChecklistStatus(status: boolean): void {
    // The authoritative value is bqStepDataService.checklistComplete(); this only clears the "touched"
    // nudge once the inspector has made every item complete via the checklist UI.
    if (status) {
      this.touched.set(false);
    }
  }

  navigateToFinalReview(): void {
    if (this.checklistComplete()) {
      this.router.navigateWithPersistedState(["../final-review"], { relativeTo: this.route });
    } else {
      this.touched.set(true);
      this.currentTab.set(DiscussAndDemonstrateTab.Checklist);
      this.toastService.error("All checklist items must be marked as reviewed to complete walkthrough.");
    }
  }

  goBack(): void {
    this.location.back();
  }

  private async handleImageDeleted(issueId: number, fileIdentifier: string): Promise<void> {
    const issue = this.openIssues().find(i => i.id === issueId);
    const media = (this.bqStepDataService.media() ?? []).find(m => m.fileIdentifier === fileIdentifier && m.parentIdentifier === issue?.identifier);
    if (!media) {
      return;
    }

    try {
      await this.bqStepDataService.deleteIssueMedia(media);
    } catch {
      this.toastService.error("Failed to delete photo. Please try again.");
    }
  }

  private resolveDisplayImages(issueIdentifier: string): (SafeUrl | undefined)[] {
    return (this.bqStepDataService.media() ?? [])
      .filter(m => m.parentIdentifier === issueIdentifier && !m.deleted)
      .slice()
      .reverse()
      .map(img => this.toSafeUrl(img));
  }

  private resolveGalleryImages(issueIdentifier: string): BqaImageItem[] {
    return (this.bqStepDataService.media() ?? [])
      .filter(m => m.parentIdentifier === issueIdentifier && (m.base64Image || m.resourceUri) && !m.deleted)
      .slice()
      .reverse()
      .map(img => new BqaImageItem({
        azureBlobUri: this.resolveBlobUri(img),
        mediaIdentifier: img.fileIdentifier,
        isThumbnailOnly: img.isThumbnailOnly || false
      }));
  }

  private resolveVendorForIssue(issue: JobIssue): VendorDTO | undefined {
    if (issue.isSelfAssigned) {
      return SELF_ASSIGNED_VENDOR;
    }

    const jobIssueVendor = (this.bqStepDataService.jobIssueVendors() ?? [])
      .find(v => v.issueIdentifier === issue.identifier && !v.deleted);
    if (!jobIssueVendor) {
      return undefined;
    }

    return (this.bqStepDataService.vendors() ?? []).find(v => v.id === jobIssueVendor.externalVendorId);
  }

  private toSafeUrl(img: Media): SafeUrl | undefined {
    const uri = this.resolveBlobUri(img);
    return uri ? this.sanitizer.bypassSecurityTrustUrl(uri) : undefined;
  }

  private resolveBlobUri(img: Media): string {
    if (img.base64Image) {
      return this.createObjectUrlAndTrack(this.blobService.base64ToBlob(img.base64Image));
    }
    return img.resourceUri ?? "";
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }
}
